// --- Package Imports ---
import { DateTime } from "luxon";

// --- Helpers for Formatting Functions ---

// Modified .isInteger() method with polyfill for IE
Number.isInteger =
  Number.isInteger ||
  function (value) {
    return (
      typeof value === "number" &&
      isFinite(value) &&
      Math.floor(value) === value
    );
  };

// --- Formatting Functions ---

export const formatPrice = (price: string): string => {
  const parsed = parseFloat(price);

  return parsed % 1 === 0 ? parsed.toFixed(2) : parseFloat(price).toString();
};

// Used for quantity fields in the table component
// styling is different for trailing zeroes therefore
// we use this to generate the characters in a nested <span>
export const generateTrailingZeroes = (quantity: string): string => {
  let length: number = 0;

  if (quantity === "0") return ".00000000";

  if (Number.isNaN(quantity)) {
    return "Invalid number";
  }

  if (Number.isInteger(parseFloat(quantity))) {
    return ".00000000";
  }

  length =
    (quantity && quantity.split(".")[1] && quantity.split(".")[1].length) || 0;

  if (length >= 8) return "";

  return new Array(9 - length).join("0");
};

export const formatCurrency = (str: string) => {
  // Remove the dollar sign (and any other non-numeric characters except the decimal point)
  let numberStr = str.replace(/[^0-9.]/g, "");

  // Parse the string to a float to handle any leading zeros
  let number = parseFloat(numberStr);

  // Check if it's a valid number
  if (isNaN(number)) {
    return "Invalid number";
  }

  // Format the number with commas and convert it back to a string
  let formattedNumber = number.toLocaleString();

  // Add the dollar sign back
  return "$" + formattedNumber;
};

export const formatIsoDateToLocale = (iso: string) => {
  return DateTime.fromISO(iso).toUTC().toFormat("LLL d, h:mm:ss a");
};

export const capitalizeFirstChars = (split_str: string[]): string => {
  return split_str
    .map((string) => {
      if (!string) return "";
      return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    })
    .join(" ");
};

// Intl.NumberFormat construction is far costlier than format(); cache the
// instances since grid valueFormatters call this per visible cell per repaint.
const defaultNumberFormat = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 8,
});
const fixedNumberFormats = new Map<number, Intl.NumberFormat>();

export const formatNumberWithCommas = (
  number: number,
  fixed: boolean,
  fixed_value?: number
): string => {
  if (!fixed) return defaultNumberFormat.format(number);
  const digits = fixed_value || 8;
  let fmt = fixedNumberFormats.get(digits);
  if (!fmt) {
    fmt = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    fixedNumberFormats.set(digits, fmt);
  }
  return fmt.format(parseFloat(number.toString()));
};

export const formatTimestamp = (iso: string): string => {
  const d = new Date(iso);
  const pad = (n: number, len = 2) => String(n).padStart(len, "0");
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}.${pad(d.getUTCMilliseconds(), 3)}`
  );
};

const DATETIME_LOCAL_RE = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?$/;

// Serialize a Date's UTC wall-clock as the `YYYY-MM-DDTHH:MM` a datetime-local
// input expects; the value is UTC, not the browser zone.
export function utcDatetimeInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

// Parse a datetime-local string as a UTC instant; returns null if malformed.
export function datetimeInputToUtcDate(s: string): Date | null {
  const m = DATETIME_LOCAL_RE.exec(s);
  if (!m) return null;
  const d = new Date(`${m[1]}${m[2] ?? ":00"}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Extract the quote asset from a Pulse symbol like "CLST.SPOT.BTC-USD" → "USD".
// Returns "" if the symbol is empty, lacks dots, or has no dash-separated tail.
export function quoteAssetFromSymbol(symbol: string | null | undefined): string {
  if (!symbol) return "";
  const tail = symbol.split(".").pop() ?? "";
  const parts = tail.split("-");
  return parts.length > 1 ? (parts[parts.length - 1] ?? "") : "";
}

export function baseAssetFromSymbol(symbol: string | null | undefined): string {
  if (!symbol) return "";
  return symbol.split(".").pop()?.split("-")[0] ?? symbol;
}

export function toNumber(v: number | string | null | undefined): number {
  if (v == null) return 0;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Like toNumber but preserves absence: null/undefined stay null instead of
// collapsing to 0, so a missing optional field reads as blank not zero.
export function optNumber(v?: string | null): number | null {
  return v == null ? null : toNumber(v);
}

export function formatSigned(value: number, suffix: string): string {
  if (!Number.isFinite(value)) return "";
  return suffix ? `${formatNumberWithCommas(value, false)} ${suffix}` : formatNumberWithCommas(value, false);
}

// Coarse buckets are enough here — this exists to answer "is this print
// recent or ancient", not to give a precise duration.
export function formatAgeMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "";
  const seconds = ms / 1000;
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.floor(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.floor(hours * 10) / 10}h`;
  return `${Math.floor((hours / 24) * 10) / 10}d`;
}

export const trimStrings = (payload: any): any => {
  if (typeof payload === "string") {
    return payload.trim();
  } else if (Array.isArray(payload)) {
    return payload.map(trimStrings);
  } else if (typeof payload === "object" && payload !== null) {
    const trimmedPayload: any = {};
    for (const key in payload) {
      if (payload.hasOwnProperty(key)) {
        trimmedPayload[key] = trimStrings(payload[key]);
      }
    }
    return trimmedPayload;
  }
  return payload;
};
